import * as React from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import {
  AddEmployeesToApprovalGroupDocument,
  ApprovalsGroupFragment,
} from 'generated/resources/graphql'
import isNil from 'lodash/isNil'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import approvalGroupMemberships from 'modules/approvals/store/collections/approvalGroupMembership'
import approvalGroups from 'modules/approvals/store/collections/approvalGroups'
import type ApprovalGroup from 'modules/approvals/store/models/ApprovalGroup'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import employees from 'modules/employees/store/collections/employees'

interface Props {
  onClose: () => void
  approvalGroupId: string
}

const AddEmployeesModal = ({ onClose, approvalGroupId }: Props) => {
  const { group } = useOutletContext<{ group: ApprovalsGroupFragment }>()
  const { loading } = useAsyncData(async () => {
    await Promise.all([
      approvalGroups.fetch({ data: { category: group.category } }),
      approvalGroupMemberships.fetch({ data: { category: group.category } }),
    ])
  }, [group.category])

  const [approvalGroup, setApprovalGroup] = React.useState<ApprovalGroup | undefined>(undefined)
  const [employeeSelection, setEmployeeSelection] =
    React.useState<EmployeeSelectorV2MultiSelectState>({
      type: 'manual',
      ids: [],
    })
  const [excludedEmployeeIds, setExcludedEmployeeIds] = React.useState<string[]>([])

  const [addEmployeesToGroup, { loading: running }] = useMutation(
    AddEmployeesToApprovalGroupDocument,
    {
      extractorFn: (data) => data.approvalsMutations.bulkAssignApprovalsApprovalGroupMembership,
      refetchQueries: ['ApprovalGroupEmployeesDataTable'],
    }
  )

  const hasSelection =
    employeeSelection.type === 'all' ||
    (employeeSelection.type === 'manual' && employeeSelection.ids.length > 0)

  const handleSubmit = async () => {
    if (!hasSelection || !approvalGroup) return

    const result = await addEmployeesToGroup({
      variables: {
        employeeSelection,
        approvalGroupId: approvalGroup.id,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      onClose()
    }

    return handled
  }

  useUnsafeEffect(() => {
    if (loading) return

    setApprovalGroup(approvalGroups.get(approvalGroupId))
  }, [approvalGroupId, loading])

  useUnsafeEffect(() => {
    if (loading || !approvalGroup) return

    const memberships = approvalGroupMemberships.filterByApprovalGroup(approvalGroup)
    // The membership model only has access_id, so we need to convert to employee_id
    const assignedEmployeeIds = memberships
      .map((membership) => {
        const employee = employees.find(
          (e) => e.attributes.access_id === membership.attributes.access_id
        )
        return employee?.id
      })
      .filter((id): id is string => !isNil(id))

    setExcludedEmployeeIds(assignedEmployeeIds)
  }, [loading, approvalGroup])

  if (loading || !approvalGroup) {
    return (
      <Box alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    )
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      title={i18n.t('settings.approvals.approval_group.employees.add_employees.title_modal')}
      description={i18n.t('settings.approvals.approval_group.employees.add_employees.description')}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('settings.approvals.approval_group.employees.add_employees.title'),
        onClick: handleSubmit,
        disabled: !hasSelection || running,
        loading: running,
      }}
    >
      <EmployeeSelectorV2
        multiple
        value={employeeSelection.type === 'manual' ? employeeSelection.ids : []}
        onChange={setEmployeeSelection}
        forcedFilters={{ excludeIds: excludedEmployeeIds }}
        options={{ asList: true, hideLabel: true }}
        hint={i18n.t('settings.approvals.approval_group.employees.add_employees.warning')}
      />
    </F0Dialog>
  )
}
export default observer(AddEmployeesModal)
